import React, { useEffect, useState } from 'react';
import {
  Table, Card, Typography, Tag, Button, Modal, Form, Input, InputNumber, Switch, message, Space, Descriptions, Row, Col
} from 'antd';
import { EditOutlined, EyeOutlined, ReloadOutlined, CrownOutlined, DatabaseOutlined, GiftOutlined } from '@ant-design/icons';
import api from '@/api/client';

const { Title, Text } = Typography;

interface UserLevel {
  id: number;
  level: number;
  name: string;
  min_recharge: number;
  max_recharge: number | null;
  storage_bytes: number;
  daily_free_standard: number;
  daily_free_hd: number;
  color: string;
  icon: string;
  description: string;
  is_active: number;
  created_at: string;
  updated_at: string;
}

interface LevelBenefit {
  id: number;
  benefit_key: string;
  benefit_name: string;
  benefit_description: string;
  category: string;
  icon: string;
  value: string;
}

const CATEGORY_COLORS: Record<string, string> = {
  storage: 'blue',
  daily_quota: 'green',
  feature: 'purple',
  discount: 'orange',
};

const CATEGORY_LABELS: Record<string, string> = {
  storage: '存储',
  daily_quota: '每日额度',
  feature: '功能权益',
  discount: '折扣权益',
};

export default function UserLevelsPage() {
  const [levels, setLevels] = useState<UserLevel[]>([]);
  const [loading, setLoading] = useState(false);
  const [detailModal, setDetailModal] = useState<{ open: boolean; level: UserLevel | null; benefits: LevelBenefit[] }>({ open: false, level: null, benefits: [] });
  const [editModal, setEditModal] = useState<{ open: boolean; level: UserLevel | null }>({ open: false, level: null });
  const [form] = Form.useForm();

  const loadLevels = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/user-levels');
      setLevels(data);
    } catch (err) {
      message.error('加载等级列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadLevels(); }, []);

  const showDetail = async (level: UserLevel) => {
    try {
      const { data } = await api.get(`/user-levels/${level.id}`);
      setDetailModal({ open: true, level: data, benefits: data.benefits || [] });
    } catch (err) {
      message.error('加载等级详情失败');
    }
  };

  const showEdit = (level: UserLevel) => {
    setEditModal({ open: true, level });
    form.setFieldsValue({
      name: level.name,
      min_recharge: level.min_recharge,
      max_recharge: level.max_recharge,
      storage_bytes: level.storage_bytes,
      daily_free_standard: level.daily_free_standard,
      daily_free_hd: level.daily_free_hd,
      color: level.color,
      description: level.description,
      is_active: level.is_active === 1,
    });
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      await api.put(`/user-levels/${editModal.level!.id}`, {
        ...values,
        is_active: values.is_active ? 1 : 0,
      });
      message.success('保存成功');
      setEditModal({ open: false, level: null });
      loadLevels();
    } catch (err) {
      message.error('保存失败');
    }
  };

  const formatBytes = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  };

  const columns = [
    {
      title: '等级',
      width: 80,
      render: (_: any, record: UserLevel) => (
        <Tag color={record.color} style={{ fontSize: 14, padding: '4px 12px' }}>
          <CrownOutlined /> Lv.{record.level}
        </Tag>
      ),
    },
    {
      title: '名称',
      dataIndex: 'name',
      width: 150,
    },
    {
      title: '累计充值门槛',
      width: 150,
      render: (_: any, record: UserLevel) => (
        <div>
          <div>¥{record.min_recharge}</div>
          {record.max_recharge && (
            <Text type="secondary" style={{ fontSize: 12 }}>上限 ¥{record.max_recharge}</Text>
          )}
        </div>
      ),
    },
    {
      title: '存储空间',
      width: 120,
      render: (_: any, record: UserLevel) => (
        <Tag icon={<DatabaseOutlined />} color="blue">{formatBytes(record.storage_bytes)}</Tag>
      ),
    },
    {
      title: '每日免费额度',
      width: 150,
      render: (_: any, record: UserLevel) => (
        <div>
          <Tag icon={<GiftOutlined />} color="green" style={{ marginBottom: 4 }}>
            标准图 {record.daily_free_standard}张
          </Tag>
          {record.daily_free_hd > 0 && (
            <Tag color="gold">高清图 {record.daily_free_hd}张</Tag>
          )}
        </div>
      ),
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      width: 80,
      render: (v: number) => (
        <Tag color={v === 1 ? 'green' : 'red'}>{v === 1 ? '启用' : '禁用'}</Tag>
      ),
    },
    {
      title: '操作',
      width: 150,
      render: (_: any, record: UserLevel) => (
        <Space>
          <Button size="small" icon={<EyeOutlined />} onClick={() => showDetail(record)}>详情</Button>
          <Button size="small" icon={<EditOutlined />} onClick={() => showEdit(record)}>编辑</Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Title level={4} style={{ marginBottom: 24 }}>用户等级管理</Title>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ReloadOutlined />} onClick={loadLevels}>刷新</Button>
      </Space>
      <Table
        columns={columns}
        dataSource={levels}
        rowKey="id"
        loading={loading}
        pagination={false}
      />

      {/* 等级详情弹窗 */}
      <Modal
        title={`Lv.${detailModal.level?.level} ${detailModal.level?.name} - 详情`}
        open={detailModal.open}
        onCancel={() => setDetailModal({ open: false, level: null, benefits: [] })}
        width={700}
        footer={null}
      >
        {detailModal.level && (
          <div>
            <Card size="small" style={{ marginBottom: 16 }}>
              <Descriptions column={2} size="small">
                <Descriptions.Item label="等级">Lv.{detailModal.level.level}</Descriptions.Item>
                <Descriptions.Item label="名称">{detailModal.level.name}</Descriptions.Item>
                <Descriptions.Item label="累计充值门槛">¥{detailModal.level.min_recharge}</Descriptions.Item>
                <Descriptions.Item label="累计充值上限">{detailModal.level.max_recharge ? `¥${detailModal.level.max_recharge}` : '无上限'}</Descriptions.Item>
                <Descriptions.Item label="存储空间">{formatBytes(detailModal.level.storage_bytes)}</Descriptions.Item>
                <Descriptions.Item label="每日标准图">{detailModal.level.daily_free_standard}张</Descriptions.Item>
                <Descriptions.Item label="每日高清图">{detailModal.level.daily_free_hd}张</Descriptions.Item>
                <Descriptions.Item label="状态">{detailModal.level.is_active === 1 ? '启用' : '禁用'}</Descriptions.Item>
              </Descriptions>
            </Card>

            <Title level={5}>权益配置</Title>
            <Row gutter={[8, 8]}>
              {detailModal.benefits.map((benefit) => (
                <Col span={12} key={benefit.id}>
                  <Card size="small">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 20 }}>{benefit.icon}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 500 }}>{benefit.benefit_name}</div>
                        <Tag style={{ fontSize: 12 }} color={CATEGORY_COLORS[benefit.category] || 'default'}>
                          {CATEGORY_LABELS[benefit.category] || benefit.category}
                        </Tag>
                      </div>
                      <Tag color="blue">{benefit.value}</Tag>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        )}
      </Modal>

      {/* 编辑弹窗 */}
      <Modal
        title="编辑等级配置"
        open={editModal.open}
        onCancel={() => setEditModal({ open: false, level: null })}
        onOk={handleSave}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="等级名称" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label="累计充值门槛">
            <Space>
              <Form.Item name="min_recharge" noStyle rules={[{ required: true }]}>
                <InputNumber prefix="¥" style={{ width: 150 }} />
              </Form.Item>
              <span>至</span>
              <Form.Item name="max_recharge" noStyle>
                <InputNumber prefix="¥" style={{ width: 150 }} placeholder="无上限" />
              </Form.Item>
            </Space>
          </Form.Item>
          <Form.Item name="storage_bytes" label="存储空间（字节）" rules={[{ required: true }]}>
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="每日免费额度">
            <Space>
              <Form.Item name="daily_free_standard" noStyle rules={[{ required: true }]}>
                <InputNumber addonBefore="标准图" style={{ width: 150 }} />
              </Form.Item>
              <Form.Item name="daily_free_hd" noStyle>
                <InputNumber addonBefore="高清图" style={{ width: 150 }} />
              </Form.Item>
            </Space>
          </Form.Item>
          <Form.Item name="color" label="等级颜色">
            <Input />
          </Form.Item>
          <Form.Item name="description" label="描述">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="is_active" label="状态" valuePropName="checked">
            <Switch checkedChildren="启用" unCheckedChildren="禁用" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
